'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useRoomContext, useLocalParticipant } from '@livekit/components-react';
import { RoomEvent } from 'livekit-client';

export type BreakoutRoom = {
  id: string;
  name: string;
  participants: string[];
};

type BreakoutMessage = 
  | { type: 'BREAKOUT_ASSIGN'; payload: { rooms: BreakoutRoom[] } }
  | { type: 'BREAKOUT_START'; payload: null }
  | { type: 'BREAKOUT_CLOSE_INIT'; payload: null }
  | { type: 'BREAKOUT_END'; payload: null }
  | { type: 'BREAKOUT_MOVE'; payload: { identity: string; targetRoomId: string | null } };

const TOPIC_BREAKOUT = 'lk-breakout';
const CLOSE_DURATION = 5;

interface BreakoutContextType {
  rooms: BreakoutRoom[];
  assignedRoomId: string | null;
  isInBreakout: boolean;
  isHost: boolean;
  closingTimer: number | null;
  isSessionActive: boolean;
  
  createRooms: (count: number) => void;
  addRoom: () => void;
  distributeAutomatically: (groupSize: number, participantIdentities: string[]) => void;
  assignParticipant: (identity: string, roomId: string) => void;
  moveParticipant: (identity: string, targetRoomId: string | null) => void;
  startBreakout: () => void;
  initiateCloseBreakout: () => void;
  returnToMainRoom: () => void;
  resetState: () => void;
}

export const BreakoutContext = createContext<BreakoutContextType | undefined>(undefined);

export function BreakoutProvider({ 
  children, 
  isHost, 
  onRoomChange 
}: { 
  children: React.ReactNode; 
  isHost: boolean;
  onRoomChange: (roomName: string | null) => void; 
}) {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();

  const [rooms, setRooms] = useState<BreakoutRoom[]>([]);
  const [assignedRoomId, setAssignedRoomId] = useState<string | null>(null);
  const [isInBreakout, setIsInBreakout] = useState(false);
  const [closingTimer, setClosingTimer] = useState<number | null>(null);
  const [isSessionActive, setIsSessionActive] = useState(false);
  
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const hostResetTimerRef = useRef<NodeJS.Timeout | null>(null);
  const roomsRef = useRef<BreakoutRoom[]>(rooms);
  roomsRef.current = rooms;
  const encoder = new TextEncoder();
  const decoder = new TextDecoder();

  const sendLocalData = useCallback((data: BreakoutMessage) => {
    if (!room?.localParticipant) return;
    const payload = encoder.encode(JSON.stringify(data));
    try {
      room.localParticipant.publishData(payload, { reliable: true, topic: TOPIC_BREAKOUT });
    } catch (err) {
      console.warn('[BreakoutContext] publishData falhou (ignorado):', err);
    }
  }, [room, encoder]);

  // --- FUNÇÃO DE LIMPEZA TOTAL ---
  const resetState = useCallback(() => {
    console.log("🧹 [Breakout] Resetando estado e limpando storage.");
    
    // Limpa estados React
    setRooms([]);
    setAssignedRoomId(null);
    setIsInBreakout(false);
    setIsSessionActive(false);
    setClosingTimer(null);
    
    if (timerRef.current) clearInterval(timerRef.current);
    if (hostResetTimerRef.current) { clearTimeout(hostResetTimerRef.current); hostResetTimerRef.current = null; }
    
    // Limpa Storage (A MUDANÇA CRÍTICA ESTÁ AQUI)
    if (room?.name) {
      localStorage.removeItem(`evolutto-breakout-${room.name}`);
      localStorage.removeItem(`evolutto-breakout-active-${room.name}`);
    }
  }, [room?.name]);

  // --- 1. CARREGAMENTO DE ESTADO ---
  // Restaura estado salvo quando o host entra na sala.
  // IMPORTANTE: NÃO chama resetState() aqui — o estado inicial já é vazio (useState([])).
  // Chamar resetState() durante a hidratação destruiria salas recém-criadas antes do save effect rodar.
  useEffect(() => {
    if (isHost && typeof window !== 'undefined' && room?.name) {
      const roomKey = room.name;
      
      const savedRooms = localStorage.getItem(`evolutto-breakout-${roomKey}`);
      const savedActive = localStorage.getItem(`evolutto-breakout-active-${roomKey}`);

      if (savedRooms) {
        try { 
          const parsedRooms = JSON.parse(savedRooms);
          if (Array.isArray(parsedRooms) && parsedRooms.length > 0) {
            if (savedActive === 'true') {
              console.log("📂 [Breakout] Restaurando sessão ativa do localStorage.");
              setRooms(parsedRooms);
              setIsSessionActive(true);
            } else {
              // Salas configuradas mas sessão ainda não iniciada — restaurar salas
              console.log("📂 [Breakout] Restaurando salas pré-configuradas (sessão inativa).");
              setRooms(parsedRooms);
            }
          } else {
            // Array vazio ou inválido — limpar localStorage
            localStorage.removeItem(`evolutto-breakout-${roomKey}`);
            localStorage.removeItem(`evolutto-breakout-active-${roomKey}`);
          }
        } catch(e) {
          // JSON inválido — limpar
          localStorage.removeItem(`evolutto-breakout-${roomKey}`);
          localStorage.removeItem(`evolutto-breakout-active-${roomKey}`);
        }
      }
      // Se não há nada salvo: estado inicial já é [] — não precisa resetar
    }
  }, [isHost, room?.name]);

  // --- 2. SALVAMENTO DE ESTADO ---
  useEffect(() => {
    if (isHost && room?.name) {
      // Só salva se tivermos salas E a sessão estiver marcada como ativa ou em configuração
      if (rooms.length > 0) {
        localStorage.setItem(`evolutto-breakout-${room.name}`, JSON.stringify(rooms));
        localStorage.setItem(`evolutto-breakout-active-${room.name}`, String(isSessionActive));
      }
    }
  }, [rooms, isHost, room?.name, isSessionActive]);

  // --- AÇÕES ---

  const createRooms = (count: number) => {
    console.log('[BreakoutContext] createRooms chamado:', count, 'isSessionActive:', isSessionActive);
    if (isSessionActive) return; 
    
    // Se mandar criar 0, limpa tudo
    if (count === 0) {
        resetState();
        return;
    }

    const newRooms: BreakoutRoom[] = Array.from({ length: count }, (_, i) => ({
      id: `breakout-${Date.now()}-${i}`,
      name: `Sala de Apoio ${i + 1}`,
      participants: []
    }));
    console.log('[BreakoutContext] setRooms com', newRooms.length, 'salas');
    setRooms(newRooms);
    sendLocalData({ type: 'BREAKOUT_ASSIGN', payload: { rooms: newRooms } });
  };

  const distributeAutomatically = (groupSize: number, participantIdentities: string[]) => {
    if (isSessionActive) return;
    if (groupSize <= 0 || participantIdentities.length === 0) return;
    
    const shuffled = [...participantIdentities].sort(() => 0.5 - Math.random());
    const numRooms = Math.ceil(shuffled.length / groupSize);
    const newRooms: BreakoutRoom[] = [];
    
    for (let i = 0; i < numRooms; i++) {
      const chunk = shuffled.slice(i * groupSize, (i + 1) * groupSize);
      newRooms.push({
        id: `breakout-${Date.now()}-${i}`,
        name: `Sala de Apoio ${i + 1}`,
        participants: chunk
      });
    }
    setRooms(newRooms);
    sendLocalData({ type: 'BREAKOUT_ASSIGN', payload: { rooms: newRooms } });
  };

  const assignParticipant = (identity: string, roomId: string) => {
    const updatedRooms = rooms.map(r => ({
      ...r,
      participants: r.id === roomId 
        ? [...new Set([...r.participants, identity])]
        : r.participants.filter(p => p !== identity)
    }));
    setRooms(updatedRooms);
    sendLocalData({ type: 'BREAKOUT_ASSIGN', payload: { rooms: updatedRooms } });
  };

  /** Adiciona uma sala vazia durante ou antes da sessão. */
  const addRoom = useCallback(() => {
    const newRoom: BreakoutRoom = {
      id: `breakout-${Date.now()}-extra${rooms.length}`,
      name: `Sala de Apoio ${rooms.length + 1}`,
      participants: [],
    };
    const updated = [...rooms, newRoom];
    setRooms(updated);
    sendLocalData({ type: 'BREAKOUT_ASSIGN', payload: { rooms: updated } });
  }, [rooms, sendLocalData]);

  /**
   * Move um participante para outra sala (ou para a sala principal se targetRoomId=null).
   * Durante sessão ativa, participantes já estão em salas LiveKit separadas — publishData
   * na sala principal NÃO alcança quem está em breakout. Usamos /api/breakout (server SDK).
   */
  const moveParticipant = useCallback(async (identity: string, targetRoomId: string | null) => {
    const moveMsg: BreakoutMessage = {
      type: 'BREAKOUT_MOVE',
      payload: { identity, targetRoomId },
    };

    const updatedRooms = rooms.map((r) => ({
      ...r,
      participants:
        r.id === targetRoomId
          ? [...new Set([...r.participants, identity])]
          : r.participants.filter((p) => p !== identity),
    }));
    setRooms(updatedRooms);
    sendLocalData({ type: 'BREAKOUT_ASSIGN', payload: { rooms: updatedRooms } });

    if (!isSessionActive) return;

    const mainRoomName = room?.name;
    const breakoutRoomIds = rooms.map((r) => r.id);
    const broadcastTargets = [
      ...(mainRoomName ? [mainRoomName] : []),
      ...breakoutRoomIds,
    ].filter((name, i, arr) => arr.indexOf(name) === i);

    try {
      const res = await fetch('/api/breakout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'BROADCAST',
          rooms: broadcastTargets,
          topic: TOPIC_BREAKOUT,
          payload: moveMsg,
        }),
      });
      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        console.error('[Breakout] API /breakout falhou:', res.status, errText);
        return;
      }
      console.log(
        `[Breakout] MOVE broadcast: ${identity} → ${targetRoomId ?? 'main'} (${broadcastTargets.length} salas)`,
      );
    } catch (err) {
      console.error('[Breakout] Falha ao enviar MOVE via server:', err);
    }
  }, [rooms, sendLocalData, isSessionActive, room?.name]);

  const startBreakout = async () => {
    if (isSessionActive) return;

    setIsSessionActive(true);
    sendLocalData({ type: 'BREAKOUT_START', payload: null });
    
    const myAssignment = rooms.find(r => r.participants.includes(localParticipant.identity));
    if (myAssignment) {
      setIsInBreakout(true);
      setAssignedRoomId(myAssignment.id);
      onRoomChange(myAssignment.id);
    }

    if (isHost) {
      try {
        const payload = {
          room_name: room.name,
          started_at: new Date().toISOString(),
          rooms: rooms.map(r => ({
            name: r.name,
            original_room_id: r.id,
            participants: r.participants
          }))
        };
        fetch(`https://meet-api.evolutto.com.br/api/breakout-sessions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload)
        }).then(async (res) => {
            if(!res.ok) console.error(await res.text());
        }).catch(e => console.error(e));
      } catch (error) { console.error(error); }
    }
  };

  const initiateCloseBreakout = async () => {
    if (!isSessionActive) return;

    sendLocalData({ type: 'BREAKOUT_CLOSE_INIT', payload: null });

    // O host NÃO inicia countdown local — apenas participantes nas salas de apoio devem fazer isso.
    // O host já está na sala principal; chamar returnToMainRoom() seria desnecessário e causaria reconexão.
    // Agendamos apenas o resetState() para limpar o estado da sessão após o countdown dos participantes.
    if (hostResetTimerRef.current) clearTimeout(hostResetTimerRef.current);
    hostResetTimerRef.current = setTimeout(() => {
      resetState();
      hostResetTimerRef.current = null;
    }, (CLOSE_DURATION + 2) * 1000); // +2s de margem

    const roomIds = rooms.map(r => r.id);
    try {
      await fetch('/api/breakout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'CLOSE_ALL',
          rooms: roomIds,
          payload: { type: 'BREAKOUT_CLOSE_INIT', payload: null }
        })
      });
    } catch (error) { console.error(error); }
  };

  const returnToMainRoom = useCallback(() => {
    console.log('BreakoutContext: Retornando à sala principal e limpando dados.');
    
    // AQUI É A CORREÇÃO:
    // Ao voltar, chamamos resetState() para garantir que tudo seja apagado
    // (Storage, Timers, Flags)
    resetState();
    
    try { onRoomChange(null); } 
    catch (error) { console.error('Erro ao retornar:', error); }
  }, [onRoomChange, resetState]);

  const startLocalCountdown = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setClosingTimer(CLOSE_DURATION);

    timerRef.current = setInterval(() => {
      setClosingTimer((prev) => {
        if (prev === null || prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          returnToMainRoom();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [returnToMainRoom]);

  // LISTENERS
  useEffect(() => {
    if (!room) return;
    const handleData = (payload: Uint8Array, participant: any, _kind: any, topic?: string) => {
      const isBreakoutMessage = !topic || topic === TOPIC_BREAKOUT;
      if (!isBreakoutMessage) return;

      try {
        const msg = JSON.parse(decoder.decode(payload)) as BreakoutMessage;
        
        if (msg.type === 'BREAKOUT_CLOSE_INIT') {
           if (closingTimer === null) {
              const currentRoomName = room?.name || '';
              const isLikelyInBreakout = isInBreakout || (currentRoomName.startsWith('breakout-'));
              // Só inicia countdown se estiver em sala de apoio — host na sala principal não precisa
              if (isLikelyInBreakout) startLocalCountdown();
           }
        }
        if (msg.type === 'BREAKOUT_ASSIGN') setRooms(msg.payload.rooms);
        if (msg.type === 'BREAKOUT_START') {
            setIsSessionActive(true);
            const currentRooms = roomsRef.current;
            const myRoom = currentRooms.find((r) =>
              r.participants.includes(localParticipant.identity),
            );
            if (myRoom) {
                setIsInBreakout(true);
                setAssignedRoomId(myRoom.id);
                onRoomChange(myRoom.id);
            }
        }
        if (msg.type === 'BREAKOUT_MOVE') {
            const { identity, targetRoomId } = msg.payload;
            if (identity === localParticipant.identity) {
              console.log(
                `[Breakout] MOVE recebido: ${localParticipant.identity} → ${targetRoomId ?? 'main'}`,
              );
              if (targetRoomId === null) {
                setIsInBreakout(false);
                setAssignedRoomId(null);
                onRoomChange(null);
              } else {
                setIsInBreakout(true);
                setAssignedRoomId(targetRoomId);
                onRoomChange(targetRoomId);
              }
            }
        }
        if (msg.type === 'BREAKOUT_END') {
            // Só volta para a sala principal se estiver em uma sala de apoio
            // O host na sala principal não deve reconectar desnecessariamente
            if (isInBreakout) {
              returnToMainRoom();
            } else {
              // Apenas limpar estado sem reconectar
              resetState();
            }
        }
      } catch (e) { console.error(e); }
    };
    room.on(RoomEvent.DataReceived, handleData);
    return () => { room.off(RoomEvent.DataReceived, handleData); };
  }, [room, rooms, localParticipant, onRoomChange, startLocalCountdown, returnToMainRoom, closingTimer, isInBreakout, decoder, isHost]);

  return (
    <BreakoutContext.Provider value={{
      rooms, assignedRoomId, isInBreakout, isHost, closingTimer, isSessionActive,
      createRooms, addRoom, distributeAutomatically, assignParticipant, moveParticipant,
      startBreakout, initiateCloseBreakout, returnToMainRoom, resetState
    }}>
      {children}
    </BreakoutContext.Provider>
  );
}

export const useBreakout = () => {
  const ctx = useContext(BreakoutContext);
  if (!ctx) throw new Error("useBreakout must be used within BreakoutProvider");
  return ctx;
};